// src/pages/ReviewsPage.jsx
import React, { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { Quote } from "lucide-react";
import { Button } from "../components/ui/button";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { Link } from "react-router-dom";

const MAX_LENGTH = 220;

const ReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    const fetchReviews = async () => {
      const { data, error } = await supabase
        .from("testimonials")
        .select("id, name, trek_name, place, review, created_at")
        .order("created_at", { ascending: false });

      if (!error) setReviews(data || []);
      setLoading(false);
    };

    fetchReviews();
  }, []);

  const toggleExpand = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <section className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4">

          {/* Header */}
          <div className="text-center mb-12">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wide">
              Reviews
            </span>
            <h1 className="text-4xl font-bold text-gray-900 mt-3">
              What Our Adventurers Say
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto mt-3">
              Real experiences shared by travelers who explored with us.
            </p>

            <Link to="/leave-review">
              <Button className="mt-6 bg-emerald-600 hover:bg-emerald-700">
                Leave a Review
              </Button>
            </Link>
          </div>

          {/* Content */}
          {loading ? (
            <p className="text-center text-gray-500">Loading reviews…</p>
          ) : reviews.length === 0 ? (
            <p className="text-center text-gray-500">
              No reviews yet. Be the first to share your experience!
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {reviews.map((review) => {
                const isLong = review.review.length > MAX_LENGTH;
                const showFull = expanded[review.id];

                return (
                  <div
                    key={review.id}
                    className="relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition"
                  >
                    <Quote className="absolute top-6 right-6 h-10 w-10 text-emerald-100" />

                    <div className="mb-4">
                      <h4 className="font-semibold text-gray-900">
                        {review.name}
                      </h4>
                      <p className="text-sm text-gray-500">
                        {review.place}
                      </p>
                    </div>

                    <p className="text-gray-600 leading-relaxed">
                      {showFull || !isLong
                        ? review.review
                        : `${review.review.slice(0, MAX_LENGTH)}...`}
                    </p>

                    {isLong && (
                      <button
                        onClick={() => toggleExpand(review.id)}
                        className="text-emerald-600 text-sm font-medium mt-2 hover:underline"
                      >
                        {showFull ? "Show less" : "Read more"}
                      </button>
                    )}

                    <div className="mt-4 pt-4 border-t">
                      <p className="text-sm font-medium text-emerald-700">
                        {review.trek_name}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ReviewsPage;
